import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import MyFormsPage from './pages/MyFormsPage';
import FormResponsePage from './pages/FormResponsePage';
import Dashboard from './pages/DashboardPage';
import ProjectsPage from './pages/Projects/ProjectsPage';
import FormBuilderPage from './pages/FormBuilderPage';
import AssignUsersPage from './pages/FormAccessManagerPage';
import ResponseEvaluationPage from './pages/ResponseEvaluationPage';
import NotAuthorized from './pages/NotAuthorized';
import { getMe } from './controllers/auth.controller';
import UserListPage from './pages/Users/UserListPage';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './layouts/DashboardLayout';
import DashboardAnalytics from './pages/DashboardAnalytics';
import ProjectHierarchyPage from './pages/Projects/ProjectHierarchyPage';
import SubjectsPage from './pages/SubjectsPage';
import SubjectResponsesPage from './pages/SubjectResponsesPage';
import FormSubjectComparePage from './pages/FormSubjectComparePage';
import FormSubjectsStatusPage from './pages/FormSubjectsStatusPage';


const ProtectedRoute = ({ isAuthenticated, role, allowedRoles, children }) => {
  // console.log('[ProtectedRoute] render', {
  //   isAuthenticated,
  //   role,
  //   allowedRoles,
  //   currentPath: window.location.pathname
  // });

  if (!isAuthenticated) {
    // console.log('[ProtectedRoute] Not authenticated → redirect to /login');
    return <Navigate to="/login" replace />;
  }
  if (!allowedRoles.includes(role)) {
    // console.log(
    //   '[ProtectedRoute] Role not allowed',
    //   'role =', role,
    //   'allowed =', allowedRoles
    // );
    return <Navigate to="/not-authorized" replace />;
  }
  // console.log('[ProtectedRoute] Access granted');
  return children;
};


const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const canManageSubjects = !!user && (user.role === 'admin' || user.canManageSubjects);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  // فقط یک بار روی mount اجرا می‌شود، برای رفرش شدن صفحه
  useEffect(() => {
    // console.log('[AuthInit] running once on mount');
    const token = localStorage.getItem('token');
    if (!token) {
      // console.log('[AuthInit] no token → guest');
      setUser(null);
      setLoading(false);
      return;
    }

    const init = async () => {
      try {
        // console.log('[AuthInit] token found, calling getMe()...');
        const me = await getMe();
        // console.log('[AuthInit] getMe() success. User:', me);
        setUser(me);
      } catch (err) {
        // console.error('[AuthInit] getMe() ERROR:', err);
        localStorage.removeItem('token');
        setUser(null);
      } finally {
        // console.log('[AuthInit] finished → setLoading(false)');
        setLoading(false);
      }
    };

    init();
  }, []);


  useEffect(() => {
    if (user) {
      // console.log('[UserState] user changed:', user);
    }
  }, [user]);

  if (loading) {
    return <div>در حال بارگذاری...</div>;
  }

  return (
    <div style={{ display: 'flex' }}>
      {user && <Sidebar user={user} onLogout={handleLogout} />}

      <div style={{ flexGrow: 1, padding: 24 }}>
        <Routes>
          <Route
            path="/login"
            element={
              <>
                {/* {console.log('[Route:/login] user =', user)} */}
                {user ? (
                  user.role === 'viewer' ? (
                    <Navigate to="/my-forms" replace />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                ) : (
                  <LoginPage setUser={setUser} />
                )}
              </>
            }
          />

          <Route path="/not-authorized" element={<NotAuthorized />} />

          <Route
            path="/"
            element={
              <>
                {/* {console.log('[Route:/] user =', user)} */}
                {!user ? (
                  <Navigate to="/login" replace />
                ) : user.role === 'viewer' ? (
                  <Navigate to="/my-forms" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </>
            }
          />

          <Route
            path="*"
            element={
              <>
                {/* {console.log('[Route:*] user =', user)} */}
                {!user ? (
                  <Navigate to="/login" replace />
                ) : user.role === 'viewer' ? (
                  <Navigate to="/my-forms" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </>
            }
          />



          {/* Viewer Routes */}
          <Route
            path="/my-forms"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['viewer']}
              >
                <DashboardLayout>
                  <MyFormsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/respond-form/:formId"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['viewer']}
              >
                <DashboardLayout>
                  <FormResponsePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin/Editor Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  <Dashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboardanalytics"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  <DashboardAnalytics />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin']}
              >
                <DashboardLayout>
                  <UserListPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  <ProjectsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hierarchy"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  <ProjectHierarchyPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/forms/:pageId/builder"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  <FormBuilderPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assign-users"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  <AssignUsersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/evaluate-responses"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  <ResponseEvaluationPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/subjects"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  {canManageSubjects ? <SubjectsPage /> : <NotAuthorized />}
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/subjects/:id/responses"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  {canManageSubjects ? <SubjectResponsesPage /> : <NotAuthorized />}
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/form-subject-compare"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['admin', 'editor']}
              >
                <DashboardLayout>
                  {canManageSubjects ? <FormSubjectComparePage /> : <NotAuthorized />}
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/forms/:formId/subjects"
            element={
              <ProtectedRoute
                isAuthenticated={!!user}
                role={user?.role}
                allowedRoles={['viewer']}
              >
                <DashboardLayout>
                  <FormSubjectsStatusPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

        </Routes>
      </div>
    </div>
  );
};

export default App;
